/**
 * CategoryExposureAlertsForm - Unified exposure alert configuration.
 *
 * All configuration stored in a single `exposure_settings` setting.
 * No Condition CRUD — everything is a single updateSetting call.
 *
 * Sections:
 * 1. Risk Exposures — web-login-sso, web-login-generic, llm-chatbot (opt-in toggles)
 * 2. Port Categories — grouped by severity (Enabled/Notify toggles)
 * 3. Custom Ports — SearchableTagInput with Notify toggle per port
 * 4. Custom Protocols — SearchableTagInput with Notify toggle per protocol
 */

import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ToggleSwitch } from '@/components/legacy-ui';
import {
  SearchableTagInput,
  Suggestion,
} from '@/components/SearchableTagInput';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { getCategoriesBySeverity } from '@/sections/settings/components/cards/exposureAlertCategories';
import {
  PORT_SUGGESTIONS,
  PROTOCOL_SUGGESTIONS,
} from '@/sections/settings/components/cards/exposureAlertConstants';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';

/**
 * Backend-aligned exposure settings structure.
 */
interface ExposureSettings {
  risk_exposures: Record<string, boolean>;
  port_categories: Record<string, boolean>;
  custom_ports: Record<string, boolean>;
  custom_protocols: Record<string, boolean>;
}

/**
 * Finding severity map — separate setting for severity overrides.
 */
interface FindingSeverityMap {
  exposure_groups: Record<string, string>;
  findings: Record<string, string>;
}

const SEVERITY_OPTIONS = [
  { value: 'E', label: 'Exposure' },
  { value: 'I', label: 'Info' },
  { value: 'L', label: 'Low' },
  { value: 'M', label: 'Medium' },
  { value: 'H', label: 'High' },
  { value: 'C', label: 'Critical' },
] as const;

function emptySeverityMap(): FindingSeverityMap {
  return { exposure_groups: {}, findings: {} };
}

/**
 * Common exposure risk types (opt-in).
 */
const RISK_EXPOSURES = [
  {
    id: 'web-login-sso',
    name: 'Web Logins (SSO)',
    description:
      'Login pages with SSO provider detected (Okta, Azure AD, etc.)',
  },
  {
    id: 'web-login-generic',
    name: 'Web Logins (Generic)',
    description: 'Login forms without SSO - admin panels, basic auth pages',
  },
  {
    id: 'llm-chatbot',
    name: 'LLM Chatbots',
    description: 'Exposed AI/LLM endpoints (Ollama, OpenAI-compatible, etc.)',
  },
];

function emptySettings(): ExposureSettings {
  return {
    risk_exposures: {},
    port_categories: {},
    custom_ports: {},
    custom_protocols: {},
  };
}

interface CategoryExposureAlertsFormProps {
  customerEmail?: string;
  onClose?: () => void;
}

export function CategoryExposureAlertsForm({
  customerEmail,
  onClose,
}: CategoryExposureAlertsFormProps) {
  const { me, friend } = useAuth();
  const effectiveEmail = customerEmail || friend || me;

  const settingQueryResource = useMemo(
    () => ({ resource: 'setting' as const }),
    []
  );
  const queryOptions = useMemo(
    () => ({ email: effectiveEmail }),
    [effectiveEmail]
  );

  const {
    data: settings,
    status: settingsStatus,
    refetch: refetchSettings,
  } = useMy(settingQueryResource, queryOptions);

  const { mutateAsync: updateSetting } = useUpdateSetting(customerEmail);

  const [localSettings, setLocalSettings] =
    useState<ExposureSettings>(emptySettings());
  const [initialSettings, setInitialSettings] =
    useState<ExposureSettings>(emptySettings());
  const [localSeverityMap, setLocalSeverityMap] =
    useState<FindingSeverityMap>(emptySeverityMap());
  const [initialSeverityMap, setInitialSeverityMap] =
    useState<FindingSeverityMap>(emptySeverityMap());
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);

  // Initialize from server state
  useEffect(() => {
    if (settingsStatus !== 'success') return;
    if (!settings) return;
    if (initializedRef.current) return;

    const raw = settings.find((s: Setting) => s.name === 'exposure_settings');
    const serverSettings: ExposureSettings = raw?.value
      ? (raw.value as ExposureSettings)
      : emptySettings();

    // Ensure all sub-maps exist
    const normalized: ExposureSettings = {
      risk_exposures: serverSettings.risk_exposures || {},
      port_categories: serverSettings.port_categories || {},
      custom_ports: serverSettings.custom_ports || {},
      custom_protocols: serverSettings.custom_protocols || {},
    };

    const rawSevMap = settings.find(
      (s: Setting) => s.name === 'finding_severity_map'
    );
    const serverSevMap: FindingSeverityMap = rawSevMap?.value
      ? (rawSevMap.value as FindingSeverityMap)
      : emptySeverityMap();
    const normalizedSevMap: FindingSeverityMap = {
      exposure_groups: serverSevMap.exposure_groups || {},
      findings: serverSevMap.findings || {},
    };

    setLocalSettings(structuredClone(normalized));
    setInitialSettings(structuredClone(normalized));
    setLocalSeverityMap(structuredClone(normalizedSevMap));
    setInitialSeverityMap(structuredClone(normalizedSevMap));
    initializedRef.current = true;
  }, [settings, settingsStatus]);

  // Change detection
  const hasChanges = useMemo(
    () =>
      JSON.stringify(localSettings) !== JSON.stringify(initialSettings) ||
      JSON.stringify(localSeverityMap) !== JSON.stringify(initialSeverityMap),
    [localSettings, initialSettings, localSeverityMap, initialSeverityMap]
  );

  // ========== Handlers ==========

  const toggleMapEntry = (
    section: keyof ExposureSettings,
    key: string,
    field: 'enabled' | 'notify',
    value: boolean
  ) => {
    setLocalSettings(prev => {
      const next = structuredClone(prev);
      const isEnableToggle = field === 'enabled';

      if (isEnableToggle) {
        if (value) {
          next[section][key] = next[section][key] ?? false;
        } else {
          delete next[section][key];
        }
        return next;
      }

      // notify toggle — only if key is enabled
      const isKeyEnabled = key in next[section];
      if (isKeyEnabled) {
        next[section][key] = value;
      }
      return next;
    });
  };

  const addCustomEntry = (
    section: 'custom_ports' | 'custom_protocols',
    key: string
  ) => {
    setLocalSettings(prev => {
      const alreadyExists = key in prev[section];
      if (alreadyExists) return prev;

      const next = structuredClone(prev);
      next[section][key] = false;
      return next;
    });
  };

  const removeCustomEntry = (
    section: 'custom_ports' | 'custom_protocols',
    key: string
  ) => {
    setLocalSettings(prev => {
      const next = structuredClone(prev);
      delete next[section][key];
      return next;
    });
  };

  const setSeverityOverride = (key: string, severity: string) => {
    setLocalSeverityMap(prev => {
      const next = structuredClone(prev);
      if (severity === 'E') {
        delete next.exposure_groups[key];
      } else {
        next.exposure_groups[key] = severity;
      }
      return next;
    });
  };

  const setFindingSeverityOverride = (
    findingName: string,
    severity: string
  ) => {
    setLocalSeverityMap(prev => {
      const next = structuredClone(prev);
      if (severity === 'E') {
        delete next.findings[findingName];
      } else {
        next.findings[findingName] = severity;
      }
      return next;
    });
  };

  // ========== Save ==========

  const handleApply = async () => {
    setIsSaving(true);
    try {
      await updateSetting({
        name: 'exposure_settings',
        value: localSettings,
        silent: true,
      });
      await updateSetting({
        name: 'finding_severity_map',
        value: localSeverityMap,
        silent: true,
      });
      await refetchSettings();
      toast.success('Exposure alerts updated');
      onClose?.();
    } catch (error) {
      console.error('Failed to update exposure alerts:', error);
      toast.error('Failed to update exposure alerts');
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = settingsStatus === 'pending';
  const isError = settingsStatus === 'error';
  const sortedCategories = useMemo(() => getCategoriesBySeverity(), []);

  // Filter suggestions to exclude already-added items
  const filteredPortSuggestions = useMemo((): Suggestion[] => {
    return PORT_SUGGESTIONS.filter(
      s => !(s.value in localSettings.custom_ports)
    );
  }, [localSettings.custom_ports]);

  const filteredProtocolSuggestions = useMemo((): Suggestion[] => {
    return PROTOCOL_SUGGESTIONS.filter(
      s => !(s.value in localSettings.custom_protocols)
    );
  }, [localSettings.custom_protocols]);

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="category-exposure-alerts-form"
    >
      {isLoading ? (
        <Skeleton className="mx-auto flex justify-center py-8" />
      ) : isError ? (
        <div
          className="flex flex-col items-center gap-3 py-8 text-center"
          data-testid="category-alerts-error-state"
        >
          <h4 className="text-chariot-text-primary text-sm font-semibold">
            Failed to load exposure settings.
          </h4>
          <p className="text-chariot-text-secondary text-sm">
            Your existing configuration could not be loaded. Retry before making
            changes to avoid overwriting it.
          </p>
          <Button
            variant="secondary"
            onClick={() => refetchSettings()}
            data-testid="category-alerts-retry-button"
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <p className="text-chariot-text-secondary text-sm">
            Configure exposure detection and notification preferences.
          </p>

          {/* Section 1: Risk Exposures */}
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">Risk Exposures</h4>
            <div className="divide-chariot-stroke border-chariot-stroke divide-y rounded-lg border">
              {RISK_EXPOSURES.map(type => {
                const enabled = type.id in localSettings.risk_exposures;
                const notify = localSettings.risk_exposures[type.id] ?? false;
                return (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-4"
                    data-testid={`exposure-row-${type.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{type.name}</span>
                      <span className="text-chariot-text-secondary text-xs">
                        {type.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Enabled
                        </span>
                        <ToggleSwitch
                          id={`${type.id}-enabled`}
                          checked={enabled}
                          onChange={checked =>
                            toggleMapEntry(
                              'risk_exposures',
                              type.id,
                              'enabled',
                              checked
                            )
                          }
                          disabled={isSaving}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Notify
                        </span>
                        <ToggleSwitch
                          id={`${type.id}-notify`}
                          checked={notify}
                          onChange={checked =>
                            toggleMapEntry(
                              'risk_exposures',
                              type.id,
                              'notify',
                              checked
                            )
                          }
                          disabled={isSaving || !enabled}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Severity
                        </span>
                        <Select
                          value={
                            localSeverityMap.exposure_groups[type.id] ?? 'E'
                          }
                          onValueChange={v => setSeverityOverride(type.id, v)}
                          disabled={isSaving || !enabled}
                        >
                          <SelectTrigger
                            className="h-7 w-28 text-xs"
                            data-testid={`${type.id}-severity`}
                          >
                            <SelectValue placeholder="Severity" />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITY_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Port Categories */}
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">Port Categories</h4>
            <div className="divide-chariot-stroke border-chariot-stroke divide-y rounded-lg border">
              {sortedCategories.map(category => {
                const enabled = category.id in localSettings.port_categories;
                const notify =
                  localSettings.port_categories[category.id] ?? false;
                return (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-4"
                    data-testid={`category-row-${category.id}`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {category.name}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            category.risk === 'critical'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : category.risk === 'high'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}
                        >
                          {category.risk}
                        </span>
                      </div>
                      <span className="text-chariot-text-secondary text-xs">
                        {category.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Enabled
                        </span>
                        <ToggleSwitch
                          id={`${category.id}-enabled`}
                          checked={enabled}
                          onChange={checked =>
                            toggleMapEntry(
                              'port_categories',
                              category.id,
                              'enabled',
                              checked
                            )
                          }
                          disabled={isSaving}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Notify
                        </span>
                        <ToggleSwitch
                          id={`${category.id}-notify`}
                          checked={notify}
                          onChange={checked =>
                            toggleMapEntry(
                              'port_categories',
                              category.id,
                              'notify',
                              checked
                            )
                          }
                          disabled={isSaving || !enabled}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-chariot-text-secondary text-xs">
                          Severity
                        </span>
                        <Select
                          value={
                            localSeverityMap.exposure_groups[category.id] ?? 'E'
                          }
                          onValueChange={v =>
                            setSeverityOverride(category.id, v)
                          }
                          disabled={isSaving || !enabled}
                        >
                          <SelectTrigger
                            className="h-7 w-28 text-xs"
                            data-testid={`${category.id}-severity`}
                          >
                            <SelectValue placeholder="Severity" />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITY_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 3: Custom Ports */}
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">Custom Ports</h4>
            <SearchableTagInput
              selectedValues={Object.keys(localSettings.custom_ports)}
              suggestions={filteredPortSuggestions}
              placeholder="Search or add port..."
              onAdd={async value => addCustomEntry('custom_ports', value)}
              onRemove={async value => removeCustomEntry('custom_ports', value)}
              disabled={isSaving}
              testId="custom-ports"
              hideSelectedTags
            />
            {Object.keys(localSettings.custom_ports).length > 0 && (
              <div className="divide-chariot-stroke border-chariot-stroke divide-y rounded-lg border">
                {Object.entries(localSettings.custom_ports).map(
                  ([port, notify]) => {
                    const findingName = `exposure-port-${port}`;
                    return (
                      <div
                        key={port}
                        className="flex items-center justify-between px-4 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              removeCustomEntry('custom_ports', port)
                            }
                            disabled={isSaving}
                            className="text-chariot-text-tertiary hover:text-chariot-text-primary"
                            aria-label={`Remove port ${port}`}
                          >
                            <XMarkIcon className="size-4" />
                          </button>
                          <span className="text-sm">Port {port}</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-chariot-text-secondary text-xs">
                              Notify
                            </span>
                            <ToggleSwitch
                              id={`custom-port-${port}-notify`}
                              checked={notify}
                              onChange={checked =>
                                toggleMapEntry(
                                  'custom_ports',
                                  port,
                                  'notify',
                                  checked
                                )
                              }
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-chariot-text-secondary text-xs">
                              Severity
                            </span>
                            <Select
                              value={
                                localSeverityMap.findings[findingName] ?? 'E'
                              }
                              onValueChange={v =>
                                setFindingSeverityOverride(findingName, v)
                              }
                              disabled={isSaving}
                            >
                              <SelectTrigger
                                className="h-7 w-28 text-xs"
                                data-testid={`custom-port-${port}-severity`}
                              >
                                <SelectValue placeholder="Severity" />
                              </SelectTrigger>
                              <SelectContent>
                                {SEVERITY_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>

          {/* Section 4: Custom Protocols */}
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">Custom Protocols</h4>
            <SearchableTagInput
              selectedValues={Object.keys(localSettings.custom_protocols)}
              suggestions={filteredProtocolSuggestions}
              placeholder="Search or add protocol..."
              onAdd={async value => addCustomEntry('custom_protocols', value)}
              onRemove={async value =>
                removeCustomEntry('custom_protocols', value)
              }
              disabled={isSaving}
              testId="custom-protocols"
              hideSelectedTags
            />
            {Object.keys(localSettings.custom_protocols).length > 0 && (
              <div className="divide-chariot-stroke border-chariot-stroke divide-y rounded-lg border">
                {Object.entries(localSettings.custom_protocols).map(
                  ([protocol, notify]) => {
                    const findingName = `exposure-${protocol}`;
                    return (
                      <div
                        key={protocol}
                        className="flex items-center justify-between px-4 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              removeCustomEntry('custom_protocols', protocol)
                            }
                            disabled={isSaving}
                            className="text-chariot-text-tertiary hover:text-chariot-text-primary"
                            aria-label={`Remove protocol ${protocol}`}
                          >
                            <XMarkIcon className="size-4" />
                          </button>
                          <span className="text-sm">{protocol}</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-chariot-text-secondary text-xs">
                              Notify
                            </span>
                            <ToggleSwitch
                              id={`custom-protocol-${protocol}-notify`}
                              checked={notify}
                              onChange={checked =>
                                toggleMapEntry(
                                  'custom_protocols',
                                  protocol,
                                  'notify',
                                  checked
                                )
                              }
                              disabled={isSaving}
                            />
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-chariot-text-secondary text-xs">
                              Severity
                            </span>
                            <Select
                              value={
                                localSeverityMap.findings[findingName] ?? 'E'
                              }
                              onValueChange={v =>
                                setFindingSeverityOverride(findingName, v)
                              }
                              disabled={isSaving}
                            >
                              <SelectTrigger
                                className="h-7 w-28 text-xs"
                                data-testid={`custom-protocol-${protocol}-severity`}
                              >
                                <SelectValue placeholder="Severity" />
                              </SelectTrigger>
                              <SelectContent>
                                {SEVERITY_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            {onClose && (
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSaving}
                data-testid="category-alerts-cancel-button"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleApply}
              disabled={!hasChanges || isSaving}
              data-testid="category-alerts-apply-button"
            >
              Apply
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
